import React, { useEffect, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Link, Skeleton, TextField, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { ReadinessChip } from '../../merchants/shared/StateChip'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { approveAgreement, errorText, fetchAgreement, rejectAgreement, saveAgreement, uploadAgreementDocument } from '../ptlSetupApi'
import { fmtDate, fmtDay } from '../constants'
import { agreementMissing, agreementStatus } from './MerchantStatus'
import { Banner } from './SetupUi'

/** Longest credit period the server accepts; 0 = advance. */
const MAX_CREDIT_DAYS = 365

/** Files the agreement document upload accepts. */
const DOC_TYPES = '.pdf,.png,.jpg,.jpeg'

const formOf = (a) => ({
  startDate: a?.startDate || '', expiryDate: a?.expiryDate || '',
  creditDays: a?.creditDays == null ? '' : String(a.creditDays), remarks: a?.remarks || '',
})

const errorsOf = (f) => {
  const e = {}
  if (!f.startDate) e.startDate = 'Enter the start date'
  if (!f.expiryDate) e.expiryDate = 'Enter the expiry date'
  else if (f.startDate && f.expiryDate < f.startDate) e.expiryDate = 'Cannot be before the start date'
  if (!/^\d+$/.test(f.creditDays.trim()) || Number(f.creditDays) > MAX_CREDIT_DAYS) e.creditDays = `Enter the credit days: a whole number from 0 (advance) to ${MAX_CREDIT_DAYS}`
  if (f.remarks.length > 300) e.remarks = 'At most 300 characters'
  return e
}

/**
 * One PTL merchant's agreement (Onboarding Health, Merchant Update hub, Done screen): start / expiry dates and credit
 * days (0 = advance; prepaid or postpaid is the merchant's wallet type), then the PTL agreement's own signed document (not
 * the merchant's shared upload), then approve or reject like KYC. Saving changed dates or credit days, or uploading a new document, puts an approved agreement back to awaiting approval. onChanged() reloads the
 * merchant list after any save.
 */
export default function AgreementDialog({ merchant, open, onClose, onChanged }) {
  const [a, setA] = useState(null)
  const [form, setForm] = useState(formOf(null))
  const [touched, setTouched] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const fileRef = useRef(null)
  const id = merchant?.id

  useEffect(() => {
    if (!open || !id) return
    setA(null); setLoadError(''); setError(''); setTouched(false); setRejecting(false); setReason('')
    fetchAgreement(id).then((r) => { setA(r); setForm(formOf(r)) }).catch((e) => setLoadError(errorText(e, 'Could not load the agreement')))
  }, [open, id])

  const done = (r) => {
    setA(r); setForm(formOf(r)); setTouched(false); setRejecting(false); setReason('')
    if (onChanged) onChanged()
  }
  const run = (what, call) => {
    setBusy(what); setError('')
    return call().then(done).catch((e) => setError(errorText(e))).finally(() => setBusy(''))
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const errors = errorsOf(form)
  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(a))
  const shown = (k) => (touched ? errors[k] : '')

  const save = () => {
    setTouched(true)
    if (Object.keys(errors).length) return
    run('save', () => saveAgreement(id, {
      startDate: form.startDate, expiryDate: form.expiryDate, creditDays: Number(form.creditDays), remarks: form.remarks.trim(), version: a?.version,
    }))
  }
  const upload = (file) => {
    if (!file) return
    setBusy('upload'); setError('')
    // Only the document changes: keep what is typed in the form (it may not be saved yet).
    uploadAgreementDocument(id, file)
      .then((r) => { setA(r); if (onChanged) onChanged() })
      .catch((e) => setError(errorText(e, 'Could not upload the document')))
      .finally(() => { setBusy(''); if (fileRef.current) fileRef.current.value = '' })
  }

  const s = agreementStatus(a)
  const submitted = a && a.approvalStatus !== 'NOT_SUBMITTED'
  const approved = a?.approvalStatus === 'APPROVED'
  // Approve needs what the server checks, and no unsaved edits (it approves what is saved).
  const approveBlock = !a?.signed ? 'Upload the signed agreement first.' : dirty ? 'Save your changes first.'
    : a?.creditDays == null ? 'Enter and save the credit days first.' : a?.validity === 'EXPIRED' ? 'Enter the new dates first: this agreement has expired.' : ''
  const close = () => { if (!busy) onClose() }

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth aria-labelledby='ptl-agreement-title'>
      <DialogTitle id='ptl-agreement-title' sx={{ ...TYPE.sectionTitle, fontSize: 16, pb: 0.5 }}>
        Agreement · {merchant?.name}
      </DialogTitle>
      <DialogContent>
        {loadError && <Banner tone='danger'>{loadError}</Banner>}
        {!a && !loadError && <Box sx={{ py: 1 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={40} />)}</Box>}
        {a && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ReadinessChip tone={s.tone} label={s.label} />
              <Typography sx={{ ...TYPE.rowHelp, color: C.inkMuted }}>{statusLine(a)}</Typography>
            </Box>
            {a.validity === 'EXPIRING' && (
              <Banner tone='warn'>Expires on {fmtDay(a.expiryDate)}. Upload the renewed agreement and enter its dates and terms.</Banner>
            )}
            {a.validity === 'EXPIRED' && (
              <Banner tone='danger'>Expired on {fmtDay(a.expiryDate)}. Upload the renewed agreement and enter its dates and terms.</Banner>
            )}
            {a.approvalNote && <Banner tone='warn'>{a.approvalNote} Approve it again.</Banner>}

            <Typography component='h3' sx={{ ...TYPE.eyebrow, color: C.inkFaint, mb: 0.75 }}>Signed agreement</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody, flex: 1, minWidth: 200 }}>
                {a.documentUrl ? (
                  <>
                    <Link href={a.documentUrl} target='_blank' rel='noopener' sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      View document<OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
                    </Link>
                    {` · ${a.documentName || 'document'}`}{a.documentUploadedAt ? `, uploaded ${fmtDate(a.documentUploadedAt)}` : ''}
                  </>
                ) : submitted ? 'No signed document yet.' : 'Save the dates and credit days first, then upload the signed agreement.'}
              </Typography>
              <input ref={fileRef} type='file' accept={DOC_TYPES} hidden onChange={(e) => upload(e.target.files[0])} />
              <Button variant='outlined' size='small' disabled={!!busy || !submitted} onClick={() => fileRef.current && fileRef.current.click()}
                startIcon={busy === 'upload' ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
                {a.signed ? 'Replace document' : 'Upload document'}
              </Button>
            </Box>
            {submitted && (
              <Typography sx={{ ...TYPE.meta, color: C.inkMuted, mt: -1.5, mb: 2 }}>
                PDF, PNG or JPG. Kept for PTL only: the merchant's D2C agreement is not changed.{a.signed ? ' A new document puts the agreement back to awaiting approval.' : ''}
              </Typography>
            )}

            <Typography component='h3' sx={{ ...TYPE.eyebrow, color: C.inkFaint, mb: 1 }}>Dates and credit days</Typography>
            <Box component='form' noValidate onSubmit={(e) => { e.preventDefault(); save() }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField size='small' fullWidth type='date' label='Start date' required InputLabelProps={{ shrink: true }} disabled={!!busy}
                    value={form.startDate} onChange={(e) => setField('startDate', e.target.value)} error={!!shown('startDate')} helperText={shown('startDate') || ' '} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size='small' fullWidth type='date' label='Expiry date' required InputLabelProps={{ shrink: true }} disabled={!!busy}
                    value={form.expiryDate} onChange={(e) => setField('expiryDate', e.target.value)} error={!!shown('expiryDate')} helperText={shown('expiryDate') || ' '} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size='small' fullWidth label='Credit days' required disabled={!!busy} inputProps={{ inputMode: 'numeric' }}
                    value={form.creditDays} onChange={(e) => setField('creditDays', e.target.value)} error={!!shown('creditDays')}
                    helperText={shown('creditDays') || 'Days from the invoice to pay; 0 = advance'} />
                </Grid>
                <Grid item xs={12}>
                  <TextField size='small' fullWidth label='Remarks' disabled={!!busy} value={form.remarks} onChange={(e) => setField('remarks', e.target.value)}
                    error={!!shown('remarks')} helperText={shown('remarks') || 'Optional'} />
                </Grid>
              </Grid>
              <button type='submit' hidden aria-hidden tabIndex={-1} />
            </Box>
            {dirty && a.approvalStatus === 'APPROVED' && (
              <Typography sx={{ ...TYPE.meta, color: C.inkMuted }}>Saving changed dates or credit days puts the agreement back to awaiting approval.</Typography>
            )}

            {/* An approved agreement can still be rejected (approved by mistake); a rejected one can be approved once fixed. */}
            {submitted && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography component='h3' sx={{ ...TYPE.eyebrow, color: C.inkFaint, mb: 1 }}>Approval</Typography>
                {rejecting ? (
                  <Box>
                    <TextField size='small' fullWidth autoFocus label='Reason for rejecting' required value={reason} disabled={!!busy}
                      onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 300 }} helperText='Shown on this screen until it is fixed' />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button variant='contained' color='error' disabled={!!busy || !reason.trim()} onClick={() => run('reject', () => rejectAgreement(id, a.version, reason.trim()))}
                        startIcon={busy === 'reject' ? <CircularProgress size={14} color='inherit' /> : null} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>
                        Reject agreement
                      </Button>
                      <Button disabled={!!busy} onClick={() => { setRejecting(false); setReason('') }} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    {!approved && (
                      <Button variant='contained' color='success' disabled={!!busy || !!approveBlock} onClick={() => run('approve', () => approveAgreement(id, a.version))}
                        startIcon={busy === 'approve' ? <CircularProgress size={14} color='inherit' /> : null} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>
                        Approve agreement
                      </Button>
                    )}
                    {a.approvalStatus !== 'REJECTED' && (
                      <Button variant='outlined' color='error' disabled={!!busy} onClick={() => setRejecting(true)} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>
                        {approved ? 'Reject (withdraw approval)' : 'Reject'}
                      </Button>
                    )}
                    {!approved && approveBlock && <Typography sx={{ ...TYPE.meta, color: C.inkMuted }}>{approveBlock}</Typography>}
                  </Box>
                )}
              </>
            )}
          </>
        )}
        {error && <Box sx={{ mt: 2 }}><Banner tone='danger'>{error}</Banner></Box>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={!!busy} sx={{ textTransform: 'none', color: C.inkMuted }}>{dirty ? 'Cancel' : 'Close'}</Button>
        <Button variant='contained' onClick={save} disabled={!a || !!busy || !dirty} sx={{ ...SX.cta, minHeight: 36 }}
          startIcon={busy === 'save' ? <CircularProgress size={14} color='inherit' /> : null}>
          {submitted ? 'Save changes' : 'Submit for approval'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Who decided and when, or what is awaited. */
function statusLine(a) {
  if (a.approvalStatus === 'NOT_SUBMITTED') return 'Enter the dates and credit days, then upload the signed agreement.'
  if (a.approvalStatus === 'APPROVED') return `Approved by ${a.approvalBy || 'a Proship admin'} on ${fmtDate(a.approvalAt)}.`
  if (a.approvalStatus === 'REJECTED') return `Rejected by ${a.approvalBy || 'a Proship admin'} on ${fmtDate(a.approvalAt)}: ${a.rejectionReason}. Fix it and approve it again.`
  const missing = agreementMissing(a)
  return missing.length ? `Needs ${missing.join(' and ')} before it can be approved.` : 'Awaiting approval by a Proship admin.'
}
